import { IconHistory } from "@tabler/icons-react"
import { Sheet, SheetContent } from "design-system/atoms/Sheet"
import { Loading } from "modules/shared/components/Loading"
import { Pagination } from "modules/shared/components/Pagination"
import { useChangeLogQuery } from "modules/shared/hooks/useChangeLogQuery"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CampaignResponseDataSchema,
  ChangeLogModel,
  CompanySchema,
  GoalResponseDataSchema,
  KnowledgeBaseItemSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { ChangeLogHeader } from "./ChangeLogHeader"
import { ChangeLogTimeline } from "./ChangeLogTimeline"

export type ChangeLogValuesByModel = {
  KnowledgeBaseItem: Pick<z.infer<typeof KnowledgeBaseItemSchema>, "content" | "title">
  Company: Pick<
    z.infer<typeof CompanySchema>,
    "description" | "products" | "toneOfVoice" | "additionalInfo"
  >
  Goal: Pick<
    z.infer<typeof GoalResponseDataSchema>,
    "assistantInstructions" | "insightsInstructions"
  >
  Campaign: Pick<z.infer<typeof CampaignResponseDataSchema>, "description" | "toneOfVoice"> & {
    nodeAdded?: string
    nodeRemoved?: string
    nodeUpdated?: string
  }
}

type ChangeLogItemProps<T extends keyof typeof ChangeLogModel> = {
  open: boolean
  onOpenChange: (open: boolean) => void
  model: T
  recordId: string
  title?: string | null
  onApplyChanges?: (values: ChangeLogValuesByModel[T]) => Promise<void>
  canApplyChanges?: (values: ChangeLogValuesByModel[T]) => boolean
  fieldsMap?: Array<{ field: keyof ChangeLogValuesByModel[T]; label: string }>
  valueFormatter?: (key: string, value: unknown) => string | null
}

const ITEMS_PER_PAGE = 10

export function ChangeLogItem<T extends keyof typeof ChangeLogModel>({
  open,
  onOpenChange,
  model,
  recordId,
  title,
  onApplyChanges: onApplyChangesProp,
  canApplyChanges: canApplyChangesProp,
  fieldsMap,
  valueFormatter,
}: ChangeLogItemProps<T>) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [count, setCount] = useState<number | undefined>()

  const {
    data: response,
    isLoading,
    refetch,
  } = useChangeLogQuery({
    model,
    id: recordId,
    page,
    total: ITEMS_PER_PAGE,
  })

  const changeLogs = response?.data ?? []
  const responseCount = response?.pageInfo?.count

  useEffect(() => {
    if (responseCount !== undefined) {
      setCount(responseCount)
    }
  }, [responseCount])

  async function handleApplyChanges(snapshot: ChangeLogValuesByModel[T]) {
    await onApplyChangesProp?.(snapshot)

    toast.success(t("shared.changeHistory.changesApplied"))

    refetch()
  }

  const hasData = !isLoading && changeLogs && changeLogs.length > 0
  const totalPages = count ? Math.ceil(count / ITEMS_PER_PAGE) : undefined

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col gap-0 p-0">
        <ChangeLogHeader title={title} />
        <div className="overflow-y-auto p-6 flex-1">
          {isLoading && <Loading />}
          {!hasData && (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
              <IconHistory
                className="text-muted-foreground opacity-50 size-12"
                strokeWidth={1.25}
              />
              <p className="text-base text-muted-foreground">
                {t("shared.changeHistory.noChanges")}
              </p>
            </div>
          )}
          {hasData && (
            <ChangeLogTimeline
              changeLogs={changeLogs}
              onApplyChanges={onApplyChangesProp ? handleApplyChanges : undefined}
              canApplyChanges={canApplyChangesProp}
              fieldsMap={fieldsMap}
              valueFormatter={valueFormatter}
            />
          )}
        </div>
        {hasData && (
          <div className="border-t p-4">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              count={count}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
